"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, Mail, MapPin, Phone } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary/10 border-2 border-primary rounded-lg max-w-2xl mx-auto">
        <p className="text-center text-lg font-semibold text-foreground mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mx-auto block hover:scale-[1.04] transition-transform duration-[80ms]">
          Send Another Message
        </Button>
      </div>;
  }
  return <div ref={formRef} className={`max-w-2xl mx-auto transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
      <Form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <FormField control={control} name="name" rules={{
          required: "Name is required",
          minLength: {
            value: 2,
            message: "Name must be at least 2 characters"
          },
          maxLength: {
            value: 50,
            message: "Name must be less than 50 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-white font-semibold">Name</FormLabel>
                <FormControl>
                  <Input placeholder="Your Name" {...field} className="bg-dark-background/50 border-primary/30 text-white placeholder:text-foreground-dark focus:border-primary focus:ring-primary/50 transition-all duration-[80ms]" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-white font-semibold">Email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="you@example.com" {...field} className="bg-dark-background/50 border-primary/30 text-white placeholder:text-foreground-dark focus:border-primary focus:ring-primary/50 transition-all duration-[80ms]" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="message" rules={{
          required: "Message is required",
          minLength: {
            value: 10,
            message: "Message must be at least 10 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-white font-semibold">Message</FormLabel>
                <FormControl>
                  <Textarea placeholder="Tell us how we can help you..." rows={6} {...field} className="bg-dark-background/50 border-primary/30 text-white placeholder:text-foreground-dark focus:border-primary focus:ring-primary/50 transition-all duration-[80ms] resize-none" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {submitError && <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-sm font-medium">{submitError}</p>
            </div>}

          <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-3 rounded-lg shadow-[0_0_20px_rgba(59,130,246,0.5)] hover:shadow-[0_0_30px_rgba(59,130,246,0.7)] hover:scale-[1.04] active:scale-100 transition-all duration-[80ms] focus:ring-2 focus:ring-primary">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-5" />
                Sending...
              </> : 'Send Message'}
          </Button>
        </div>
      </Form>
    </div>;
}
export default function GetInTouch() {
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: infoRef,
    isVisible: infoVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section className="py-20 px-4 bg-dark-background">
      <div className="container mx-auto max-w-6xl">
        <div ref={headingRef} className={`text-center mb-12 transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] ${headingVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
            Get In <span className="text-primary-dark">Touch</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto !text-[hsl(0,0%,100%)]">
            Ready to remix your workflow? Reach out and let's eliminate that administrative friction together.
          </p>
        </div>

        <FormProvider className="mb-16" formId="contact-form-hero" sectionName="get-in-touch" defaultValues={{
        name: '',
        email: '',
        message: ''
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 50
        },
        email: {
          required: true,
          pattern: 'email'
        },
        message: {
          required: true,
          minLength: 10
        }
      }} submitText="Send Message" successMessage="Thank you! We'll get back to you soon." errorMessage="Failed to submit form. Please try again.">
          <ContactForm />
        </FormProvider>

        <div ref={infoRef} className={`grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] ${infoVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/20 mb-4">
              <MapPin className="size-6 text-primary" />
            </div>
            <h3 className="text-white font-semibold mb-2">Address</h3>
            <p className="text-white text-sm">Oklahoma City, Ok</p>
          </div>

          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary/20 mb-4">
              <Phone className="size-6 text-secondary" />
            </div>
            <h3 className="text-white font-semibold mb-2">Phone</h3>
            <p className="text-white text-sm">405-762-0732</p>
          </div>

          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent/20 mb-4">
              <Mail className="size-6 text-accent-dark" />
            </div>
            <h3 className="text-white font-semibold mb-2">Email</h3>
            <p className="text-white text-sm">info@agenticartist.net</p>
          </div>
        </div>
      </div>
    </section>;
}